import { findNearestCity } from '../data/saudiWeatherData';
import type { BillData, FinancialData, LocationData, SolarDesign, UserType } from '../types';
import { getEffectiveConsumption } from '../utils/consumptionNormalization';
import type { BOQSummary } from './boqCalculationService';
import { generateCalculationSourcesPDF, type CalculationSourcesData } from './calculationSourcesReport';
import { uploadPdfAndRecord } from './projectDocuments';
import { generatePDFReport } from './reportGenerator';
import type { SolarDesignPersistExtras } from '../components/SolarDesignView';

function modulePickFromSnapshot(
  s: Record<string, unknown> | null
): { manufacturer: string; model: string; power_stc: number } | null {
  if (!s) return null;
  const manufacturer = typeof s.manufacturer === 'string' ? s.manufacturer : String(s.manufacturer ?? '');
  const model = typeof s.model === 'string' ? s.model : String(s.model ?? '');
  const raw = s.power_stc;
  const power_stc = typeof raw === 'number' ? raw : Number(raw);
  if (!manufacturer.trim() && !model.trim()) return null;
  return {
    manufacturer,
    model,
    power_stc: Number.isFinite(power_stc) ? power_stc : 550,
  };
}

function inverterPickFromSnapshot(
  s: Record<string, unknown> | null
): { manufacturer: string; model: string; power_ac_nominal: number } | null {
  if (!s) return null;
  const manufacturer = typeof s.manufacturer === 'string' ? s.manufacturer : String(s.manufacturer ?? '');
  const model = typeof s.model === 'string' ? s.model : String(s.model ?? '');
  const raw = s.power_ac_nominal;
  const power_ac_nominal = typeof raw === 'number' ? raw : Number(raw);
  if (!manufacturer.trim() && !model.trim()) return null;
  return {
    manufacturer,
    model,
    power_ac_nominal: Number.isFinite(power_ac_nominal) ? power_ac_nominal : 0,
  };
}

export type AutoUploadSolarDesignPdfsInput = {
  projectId: string;
  pvDesignId: string | null;
  design: SolarDesign;
  financials: FinancialData;
  boq: BOQSummary;
  extras: SolarDesignPersistExtras;
  location: LocationData;
  billData: BillData | undefined;
  userType: UserType;
};

/**
 * After PV design is saved, generates the same PDFs as the Solar Design step buttons
 * and uploads them without triggering a browser download.
 */
export async function autoUploadSolarDesignPdfsAfterSave(input: AutoUploadSolarDesignPdfsInput): Promise<void> {
  const { projectId, pvDesignId, design, financials, boq, extras, location, billData, userType } = input;

  const lat = location.locations?.[0]?.latitude ?? location.latitude ?? 24.7136;
  const lon = location.locations?.[0]?.longitude ?? location.longitude ?? 46.6753;
  const cityData = findNearestCity(lat, lon);
  const projectNameBase = `Solar_Project_${new Date().toISOString().split('T')[0]}`;
  const selectedModule = modulePickFromSnapshot(extras.moduleSnapshot);
  const selectedInverter = inverterPickFromSnapshot(extras.inverterSnapshot);

  try {
    const monthlyResults = design.monthlyResults;
    if (monthlyResults && monthlyResults.length > 0) {
      const blob = await generatePDFReport(
        {
          projectName: projectNameBase,
          locationName: cityData.name,
          latitude: location.locations?.[0]?.latitude || location.latitude || 24.7136,
          longitude: location.locations?.[0]?.longitude || location.longitude || 46.6753,
          altitude: cityData.altitude,
          tilt: location.locations?.[0]?.tilt || location.tilt || 10,
          azimuth: location.locations?.[0]?.azimuth || location.azimuth || 180,
          mountingType: location.locations?.[0]?.mounting_type || location.mountingType || 'roof-fixed',
          surfaceType: location.locations?.[0]?.surface_type || location.surfaceType || 'asphalt',
          monthlyConsumption: getEffectiveConsumption(billData).effectiveMonthlyConsumption,
          systemPower: design.systemSize,
          annualProduction: design.yearlyProduction,
          specificProduction: design.yearlyProduction / design.systemSize,
          performanceRatio: monthlyResults.reduce((sum, m) => sum + m.performanceRatio, 0) / 12,
          monthlyResults: monthlyResults.map((m) => ({
            month: m.month,
            globHor: m.ghi,
            diffHor: m.ghi * 0.3,
            tempAmb: m.temp,
            globInc: m.poa,
            globEff: m.poa,
            eArray: m.arrayOutput,
            eGrid: m.gridOutput,
            pr: m.performanceRatio,
          })),
          systemConfig: {
            modulesPerString: Math.ceil(design.panelCount / 2),
            stringsParallel: 2,
            nInverters: (boq?.items.find((i) => i.category === 'Inverters')?.quantity as number | undefined) ?? 1,
            modulePower: selectedModule?.power_stc || 550,
            inverterPower:
              design.selectedInverterAcKw ??
              (selectedInverter?.power_ac_nominal ?? design.systemSize / 1.3),
            totalModules: design.panelCount,
            dcAcRatio: design.dcAcRatioFinal,
          },
          boq: boq || undefined,
        },
        { disposition: 'blob' }
      );
      if (blob instanceof Blob) {
        await uploadPdfAndRecord({
          projectId,
          designId: pvDesignId,
          docType: 'solar_report_pdf',
          blob,
          storageFileBase: projectNameBase,
          filesAppendType: 'solar_report_pdf',
        });
      }
    }
  } catch (e) {
    console.error('autoUploadSolarDesignPdfsAfterSave (solar report):', e);
  }

  try {
    const monthlyResults = design.monthlyResults;
    if (monthlyResults && monthlyResults.length > 0 && extras.calculationResults) {
      const effectiveConsumption = getEffectiveConsumption(billData);
      const monthlyConsumption = effectiveConsumption.effectiveMonthlyConsumption;
      const yearlyConsumption = effectiveConsumption.effectiveYearlyConsumption;
      const fin = financials ?? {
        totalCost: 0,
        paybackPeriod: 0,
        roi: 0,
        savingsYear1: 0,
        savings25Year: 0,
        monthlyPayment: 0,
      };

      const sourcesData: CalculationSourcesData = {
        projectName: projectNameBase,
        locationName: cityData.name,
        latitude: lat,
        longitude: lon,
        altitude: cityData.altitude,
        tilt: location.locations?.[0]?.tilt ?? location.tilt ?? 10,
        azimuth: location.locations?.[0]?.azimuth ?? location.azimuth ?? 180,
        mountingType: location.locations?.[0]?.mounting_type ?? location.mountingType ?? 'roof-fixed',
        surfaceType: location.locations?.[0]?.surface_type ?? location.surfaceType ?? 'asphalt',
        userType: userType ?? 'residential',
        monthlyConsumption,
        yearlyConsumption,
        baseYearlyConsumption: effectiveConsumption.baseYearlyConsumption,
        morningConsumptionPct: effectiveConsumption.morningConsumptionPct,
        effectiveYearlyConsumption: effectiveConsumption.effectiveYearlyConsumption,
        customTariffSarPerKwh: billData?.customTariffSarPerKwh,
        gridPhase: location.electrical?.gridPhase,
        acCableDistanceM: location.electrical?.acCableDistanceM,
        dcCableDistanceM: location.electrical?.dcCableDistanceM,
        design: {
          systemSize: design.systemSize,
          panelCount: design.panelCount,
          yearlyProduction: design.yearlyProduction,
          offset:
            yearlyConsumption > 0
              ? Math.min(100, Math.round((design.yearlyProduction / yearlyConsumption) * 100))
              : 0,
          initialSystemSize: design.initialSystemSize,
          inverterAcCapKw: design.inverterAcCapKw,
          selectedInverterAcKw: design.selectedInverterAcKw,
          selectedInverterCount: design.selectedInverterCount,
          dcAcRatioFinal: design.dcAcRatioFinal,
          monthlyResults: design.monthlyResults,
        },
        calculationResults: extras.calculationResults,
        financials: {
          totalCost: fin.totalCost,
          paybackPeriod: fin.paybackPeriod,
          roi: fin.roi,
          savingsYear1: fin.savingsYear1,
          savings25Year: fin.savings25Year,
          monthlyPayment: fin.monthlyPayment,
        },
        boq,
        selectedModule,
        selectedInverter,
      };

      const blob = await generateCalculationSourcesPDF(sourcesData, { disposition: 'blob' });
      if (blob instanceof Blob) {
        await uploadPdfAndRecord({
          projectId,
          designId: pvDesignId,
          docType: 'calculation_sources_pdf',
          blob,
          storageFileBase: `${projectNameBase}_calc_sources`,
          filesAppendType: 'calculation_sources_pdf',
        });
      }
    }
  } catch (e) {
    console.error('autoUploadSolarDesignPdfsAfterSave (calculation sources):', e);
  }
}
